import React, { useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { VoiceLayout } from './VoiceLayout';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const BLOCKS = [
  'Greeting', 'Verification Question', 'Balance Statement', 'Payment Option',
  'Installment Offer', 'Hardship Question', 'Dispute Question', 'Confirmation',
  'Escalation', 'Compliance Warning', 'Closing',
];

const DEFAULT_FLOW = [
  { id: 'b1', type: 'Greeting',             utter: 'السلام عليكم {{customer_name}}، معك {{agent_name}} من {{client_name}}.', nextIntent: 'greeted', vars: ['customer_name','agent_name','client_name'] },
  { id: 'b2', type: 'Verification Question', utter: 'ممكن تأكد لي آخر أربع أرقام من رقم الحساب وتاريخ الميلاد؟',           nextIntent: 'verified', vars: ['customer_name'] },
  { id: 'b3', type: 'Balance Statement',     utter: 'حسابك عليه مبلغ مستحق قدره {{account_balance}} ومتأخر {{days_past_due}} يوم.', nextIntent: 'understood', vars: ['account_balance','days_past_due'] },
  { id: 'b4', type: 'Payment Option',        utter: 'تقدر تسدد كامل أو نرتب خطة سداد بست أقساط بدون رسوم.',                  nextIntent: 'wants_plan', vars: ['eligible_plan'] },
  { id: 'b5', type: 'Confirmation',          utter: 'تمام، اتفقنا على {{committed_amount}} بتاريخ {{payment_date}}.',         nextIntent: 'confirmed',  vars: ['committed_amount','payment_date'] },
  { id: 'b6', type: 'Closing',               utter: 'شكراً لتعاونك ووقتك. نتمنى لك يوم طيب.',                                  nextIntent: null, vars: [] },
];

export default function ConversationDesigner() {
  usePageTitle('Conversation Designer');
  const [flow, setFlow] = useState(DEFAULT_FLOW);
  const [selected, setSelected] = useState(DEFAULT_FLOW[0].id);
  const block = flow.find((b) => b.id === selected);

  const addBlock = (type) => {
    const id = `b${Date.now()}`;
    setFlow([...flow, { id, type, utter: '', nextIntent: null, vars: [] }]);
    setSelected(id);
  };

  return (
    <VoiceLayout
      title="Conversation designer"
      subtitle="Compose the conversation block-by-block. Each block carries an agent utterance, expected intents, dynamic variables, escalation conditions and compliance notes."
      actions={(<Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><Save className="h-4 w-4 mr-1.5" aria-hidden="true" />Save flow</Button>)}
    >
      <div className="grid lg:grid-cols-12 gap-3">
        {/* Block library */}
        <aside className="lg:col-span-3 surface-card p-3 space-y-1.5 h-fit">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Block library</p>
          {BLOCKS.map((b) => (
            <button key={b} onClick={() => addBlock(b)} className="w-full flex items-center justify-between text-[12px] px-2 py-1.5 rounded-md border border-border hover:border-primary/40 hover:bg-primary/5">
              <span>{b}</span><Plus className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            </button>
          ))}
        </aside>

        {/* Canvas */}
        <section className="lg:col-span-6 surface-card p-4">
          <p className="text-[10px] uppercase tracking-widest text-primary mb-3">Flow canvas</p>
          <ol className="space-y-2">
            {flow.map((b, i) => (
              <li key={b.id}>
                <button onClick={() => setSelected(b.id)} className={cn('w-full text-left rounded-lg border p-3 flex items-start gap-3 transition-colors',
                  selected === b.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40')}>
                  <span className="font-mono-num text-[11px] text-muted-foreground shrink-0 mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  <div className="flex-1 min-w-0">
                    <Badge variant="outline" className="text-[10px] mb-1">{b.type}</Badge>
                    <p className="text-[12.5px]" dir="rtl">{b.utter || <em className="text-muted-foreground/70">empty</em>}</p>
                  </div>
                </button>
              </li>
            ))}
          </ol>
        </section>

        {/* Block settings */}
        <aside className="lg:col-span-3 surface-card p-4 space-y-3 h-fit">
          <p className="text-[10px] uppercase tracking-widest text-primary">Block settings</p>
          {block && (
            <>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</p><p className="text-[12.5px] font-medium">{block.type}</p></div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Agent utterance</p>
                <Textarea rows={4} dir="rtl" value={block.utter}
                  onChange={(e) => setFlow((f) => f.map((b) => b.id === block.id ? { ...b, utter: e.target.value } : b))} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Dynamic variables</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {block.vars.length ? block.vars.map((v) => <Badge key={v} variant="outline" className="text-[10px] font-mono-num">{`{{${v}}}`}</Badge>) : <span className="text-[11px] text-muted-foreground">None</span>}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Next-step intent</p>
                <p className="text-[12.5px]">{block.nextIntent || <em className="text-muted-foreground/70">End</em>}</p>
              </div>
              <div className="rounded-md border border-warning/40 bg-warning/5 p-2 text-[11px] text-warning">
                Compliance: this block must respect contact-window and identity-verification rules.
              </div>
              {flow.length > 1 && (
                <Button variant="outline" size="sm" className="w-full h-7 text-[11px]" onClick={() => { setFlow((f) => f.filter((b) => b.id !== block.id)); setSelected(flow[0].id); }}>
                  <Trash2 className="h-3 w-3 mr-1" aria-hidden="true" />Remove block
                </Button>
              )}
            </>
          )}
        </aside>
      </div>
    </VoiceLayout>
  );
}
